import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import toast from "react-hot-toast";
import { useLoginCreateMutation, useProfileReadQuery } from "../store/api.ts";
import { getFirstError } from "../utils.js";

const genderTypes = [
  { label: "Men's", value: "Male" },
  { label: "Women's", value: "Female" },
];

const QuizIntro = () => {
  const [selectedGender, setSelectedGender] = useState(null);
  const [autoLoginDone, setAutoLoginDone] = useState(false);
  const [showRetakeModal, setShowRetakeModal] = useState(false);
  const [pendingShopifyPayload, setPendingShopifyPayload] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [login, { isLoading: isAutoLoginLoading }] = useLoginCreateMutation();
  const { refetch } = useProfileReadQuery();

  const handleContinue = () => {
    if (selectedGender)
      navigate("/quiz1", { state: { genderType: selectedGender } });
  };

  useEffect(() => {
    const onKey = (e) => e.key === "Enter" && handleContinue();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedGender]);

  useEffect(() => {
    const run = async () => {
      try {
        const params = new URLSearchParams(
          location.search || window.location.search
        );
        const tokenFromQuery = params.get("token");
        if (!tokenFromQuery) {
          setAutoLoginDone(true);
          return;
        }
        let decoded;
        try {
          decoded = jwtDecode(tokenFromQuery);
        } catch (e) {
          console.error("Failed to decode survey token", e);
          setAutoLoginDone(true);
          return;
        }

        if (decoded?.survey_retake === true) {
          setPendingShopifyPayload({
            tokenFromQuery,
            decoded,
          });
          setShowRetakeModal(true);
          return;
        }

        await proceedAutoLoginFromDecoded(decoded);
      } catch (err) {
        console.error("Auto login from Shopify token failed", err);
        toast.error(getFirstError(err?.data || "Failed to auto login"));
        setAutoLoginDone(true);
      }
    };

    run();
  }, [location.search, login, refetch]);

  if (!autoLoginDone && isAutoLoginLoading) {
    return (
      <div className="min-h-screen w-full bg-[#EDE3DD] flex items-center justify-center">
        <p className="text-sm text-[#4a4a4a]">Setting up your account...</p>
      </div>
    );
  }

  const proceedAutoLoginFromDecoded = async (decoded) => {
    try {
      const email = decoded.email;
      const password = decoded.password;
      const token = decoded.evenessToken;

      if (token) {
        localStorage.setItem("authToken", token);
      } else {
        if (!email || !password) {
          console.warn("Survey token missing email or password");
          setAutoLoginDone(true);
          return;
        }
        const response = await login({ login: { email, password } }).unwrap();
        if (response.token) localStorage.setItem("authToken", response.token);
      }

      await refetch();
      toast.success("Logged in successfully");
    } catch (err) {
      console.error("Auto login from Shopify token failed", err);
      toast.error(getFirstError(err?.data || "Failed to auto login"));
    } finally {
      setAutoLoginDone(true);
    }
  };

  const handleRetakeYes = async () => {
    setShowRetakeModal(false);
    if (
      !pendingShopifyPayload?.decoded ||
      !pendingShopifyPayload?.tokenFromQuery
    ) {
      setAutoLoginDone(true);
      return;
    }
    await proceedAutoLoginFromDecoded(pendingShopifyPayload.decoded);
  };

  const handleRetakeNo = () => {
    try {
      const redirectUrl = pendingShopifyPayload?.decoded?.redirect_url;
      localStorage.removeItem("shopifySurveyToken");
      localStorage.removeItem("authToken");
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }
    } catch (e) {
      console.error("Failed to decode survey token", e);
      localStorage.removeItem("shopifySurveyToken");
    }
  };

  return (
    <div
      className={[
        "min-h-screen w-full",
        "bg-[#EDE3DD]",
        "flex items-start justify-center",
      ].join(" ")}
    >
      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pt-28 pb-12">
        {/* Heading */}
        <h1 className="text-center font-semibold text-xl sm:text-2xl md:text-3xl leading-tight mb-3">
          Which Department Do You Mostly Shop In?
        </h1>
        <span className="mx-auto max-w-2xl text-center text-[13px] sm:text-sm md:text-base text-[#6f6f6f] mb-10">
          We use this selection to train our algorithm on the necessary inputs for features like our “does this fit me” algorithm.
        </span>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 md:gap-10 place-items-center mt-20 mb-10">
          {genderTypes.map((type,index) => {
            const isSelected = selectedGender === type.value;
            return (
              <button
                key={index}
                type="button"
                onClick={() => setSelectedGender(type.value)}
                aria-pressed={isSelected}
                className={[
                  "w-full max-w-md",
                  "rounded-2xl bg-white/70 backdrop-blur-md",
                  "shadow-[0_10px_30px_rgba(0,0,0,0.06)]",
                  "px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-12",
                  "transition-transform duration-150 hover:scale-[0.995] active:scale-95",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-black/70",
                ].join(" ")}
              >
                <div className="mx-auto w-full max-w-xs rounded-xl bg-white/80 backdrop-blur shadow-[0_8px_20px_rgba(0,0,0,0.05)] px-6 py-6 sm:px-8 sm:py-8 text-center">
                  <span className={`text-lg sm:text-xl md:text-2xl ${isSelected ? 'text-black font-bold' : 'text-[#4a4a4a] font-semibold'}`}>
                    {type.label}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Continue button */}
        <div className="mt-24 flex justify-center mb-8">
          <button
            onClick={handleContinue}
            disabled={!selectedGender || isAutoLoginLoading}
            className={`w-[262px] h-[51px] rounded-[8px] bg-[#22272A] shadow-[0px_0px_16px_0px_#00000004] backdrop-blur-[36px] text-white transition-all ${
              !selectedGender || isAutoLoginLoading
                ? "opacity-50 cursor-not-allowed"
                : "hover:opacity-90"
            }`}
          >
            Continue
          </button>
        </div>
      </div>
      {showRetakeModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={handleRetakeNo}
          />
          <div className="relative w-[92%] max-w-md rounded-2xl bg-white shadow-xl p-6">
            <h2 className="text-lg font-semibold text-[#22272A]">
              Survey already completed
            </h2>
            <p className="mt-2 text-sm text-[#4a4a4a] leading-relaxed">
              You have already performed the survey. Are you sure you want to do
              it again?
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleRetakeNo}
                className="h-10 px-4 rounded-lg bg-white border border-black/10 text-[#22272A] hover:bg-black/5"
              >
                No
              </button>
              <button
                type="button"
                onClick={handleRetakeYes}
                className="h-10 px-4 rounded-lg bg-[#22272A] text-white hover:opacity-90"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizIntro;
